import { TokenRow, LANGUAGE_CHANGE_BREAK_TOKEN_TEXT } from './TokenRow'
import { sourceStyles, translationDevStyles, type TranscriptStreamStyles } from './transcript-stream-styles'
import { endsSentence, splitSentences } from '@/utils/sentenceLines'
import type { RealtimeToken } from '@soniox/client'

/** A pane can mix original and translated tokens (two_way mode), so each
 * token is styled by its own `translation_status` rather than by which pane
 * it happens to be displayed in — matching one_way mode, where a pane's
 * tokens are all the same status anyway. */
function stylesForToken(token: RealtimeToken): TranscriptStreamStyles {
  return token.translation_status === 'translation' ? translationDevStyles : sourceStyles
}

/** How long a single speaker may keep talking before a periodic timestamp
 * breaks in anyway, so a long monologue still carries time markers instead of
 * just the one from when the speaker started. */
const PERIODIC_MARKER_MS = 30_000

/** Whether a token renders transcript text a tint could sit on. `<end>` draws a
 *  bare italic marker and `<lang-break>` draws nothing at all, yet either can
 *  arrive `is_final`. */
function carriesText(token: RealtimeToken): boolean {
  return token.text !== '<end>' && token.text !== LANGUAGE_CHANGE_BREAK_TOKEN_TEXT
}

/** How far back from the newest segment the same speaker's earlier segments
 *  still count as part of the same utterance.
 *
 *  A person routinely says several sentences in one breath, and the proxy
 *  flushes that as several segments — so the tint has to cover a stretch of
 *  speech, not the sentence that happens to be last. Fifteen seconds is a
 *  guess at "one thing someone said", deliberately shorter than
 *  `PERIODIC_MARKER_MS`: at Output's tinted size, thirty seconds of speech is
 *  most of the pane lit at once, which marks nothing. */
const NEWEST_UTTERANCE_WINDOW_MS = 15_000

/** The span of the Output pane currently wearing the tint. Sentences before
 *  `sentenceIndex` inside `startIndex`'s token belong to an earlier sentence. */
interface NewestUtterance {
  startIndex: number
  sentenceIndex: number
  endIndex: number
}

/** Tokens carry a client-stamped arrival time alongside the proxy's own
 *  timing; see `received_ms` in `useProxySttRecording`. */
interface TimedToken extends RealtimeToken {
  received_ms?: number
}

/** Gap between two readings of one clock, or null unless both are present. */
function gapBetween(newer: number | undefined, older: number | undefined): number | null {
  if (typeof newer !== 'number' || typeof older !== 'number') return null
  return newer - older
}

/**
 * Whether `candidate` belongs to the same utterance as `newest`, measured on
 * whichever clock the two segments share.
 *
 * `start_ms` is when the words were spoken and is the better answer — but the
 * proxy puts no `start_time` on `translation.final`, the event the Output pane
 * is built from, so on the live path it is usually absent and this used to
 * answer no every time. `received_ms` is stamped by the client as each event
 * lands: one translation latency behind the speech, present on every live
 * token, and comparable because it is a single clock.
 *
 * The two are never mixed. A `start_ms` on one segment and a `received_ms` on
 * the other measure from different origins, and subtracting them would produce
 * a number that means nothing. With neither clock shared — archived tokens, for
 * one — the answer is no and the sentence walk takes over.
 */
function withinUtteranceWindow(newest: RealtimeToken, candidate: RealtimeToken): boolean {
  const spoken = gapBetween(newest.start_ms, candidate.start_ms)
  if (spoken !== null) return spoken <= NEWEST_UTTERANCE_WINDOW_MS

  const arrived = gapBetween((newest as TimedToken).received_ms, (candidate as TimedToken).received_ms)
  if (arrived !== null) return arrived <= NEWEST_UTTERANCE_WINDOW_MS

  return false
}

/**
 * Locates the newest utterance in the pane: everything one speaker has said in
 * the last few seconds, up to and including the last finalized segment.
 *
 * Neither a segment nor a sentence is the right unit on its own, and picking
 * either leaves the tint wrong in the opposite direction. A segment is whatever
 * the proxy happened to flush, and a sentence routinely straddles several of
 * them, so tinting the last segment lights a fragment mid-line. But one thing
 * someone says is routinely several sentences, so tinting the last sentence
 * lights only the tail of what they just said. Both were reported. The unit
 * that satisfies both is the stretch of speech itself.
 *
 * Two walks, in order:
 *
 * 1. Back through the same speaker's segments while they stay inside
 *    `NEWEST_UTTERANCE_WINDOW_MS` — the utterance proper. A speaker change ends
 *    it outright.
 * 2. Then, only if the first walk left the group opening at a segment boundary,
 *    back far enough that the tint never begins mid-sentence. This is where a
 *    sentence spread across segments gets picked up whole, and it is all that
 *    is left when neither clock is shared — an archived transcript, say.
 *
 * The walk stops at a draft (an untinted gap in the middle of the run would
 * look worse than a short highlight) and at `<end>` / `<lang-break>`, neither
 * of which `endsSentence` recognises as a terminator even though both end
 * whatever came before them.
 *
 * @returns The tinted span, or null while nothing has finalized yet.
 */
function findNewestUtterance(tokens: RealtimeToken[]): NewestUtterance | null {
  let endIndex = -1
  for (let idx = tokens.length - 1; idx >= 0; idx -= 1) {
    if (tokens[idx].is_final && carriesText(tokens[idx])) {
      endIndex = idx
      break
    }
  }
  if (endIndex < 0) return null

  const newest = tokens[endIndex]
  let startIndex = endIndex
  // Zero, not the last sentence break: the whole closing segment belongs to
  // what was just said, however many sentences the speaker fitted into it.
  let sentenceIndex = 0

  while (startIndex > 0) {
    const previous = tokens[startIndex - 1]
    if (!previous.is_final || !carriesText(previous)) break
    // `undefined !== undefined` is false, so a stream with no diarization
    // treats every segment as the same speaker and leans on the window alone.
    if (previous.speaker !== newest.speaker) break
    if (!withinUtteranceWindow(newest, previous)) break
    startIndex -= 1
  }

  while (sentenceIndex === 0 && startIndex > 0) {
    const previous = tokens[startIndex - 1]
    if (!previous.is_final || !carriesText(previous) || endsSentence(previous.text)) break
    startIndex -= 1
    // More than one sentence in this segment means the tint opens inside it, at
    // the last break: the rest closed before the sentence being completed here.
    sentenceIndex = splitSentences(previous.text).length - 1
  }

  return { startIndex, sentenceIndex, endIndex }
}

/**
 * Renders one row per token, tracking speaker/language breaks across the
 * whole list. Kept in its own (non-component) module — rather than inlined in
 * `Renderer.tsx` — so both the live `Renderer` and other live panels (e.g. the
 * "Output (Batch)" panel in `MeetingInsights`) can render a token stream with
 * identical per-token styling/speaker-break behavior without duplicating this
 * logic, and without triggering a fast-refresh warning on a component file.
 */
export function buildTokenRows(
  tokens: RealtimeToken[],
  keyPrefix: string,
  showTimestamp?: boolean,
  highlightOnArrival?: boolean
): React.ReactNode[] {
  let lastSpeaker: string | undefined
  let lastLanguage: string | undefined
  // A sentence often straddles two segments, so whether a token opens a new line is decided by
  // the PREVIOUS token's ending, not by anything inside this one. Tracked here rather than in
  // `TokenRow` because only this loop can see the neighbouring tokens.
  let previousEndedSentence = false
  // Elapsed-time offset of the last marker actually shown (via the speaker
  // label or a periodic marker) — the anchor the 30s periodic check counts
  // from. Reset implicitly on every speaker change since that always shows
  // its own marker and updates this.
  let lastMarkerMs: number | undefined
  // Only the Output pane tints, so the Input pane never pays for the walk.
  const newestUtterance = highlightOnArrival ? findNewestUtterance(tokens) : null

  return tokens.map((token, idx) => {
    const isNewSpeaker = token.speaker != null && token.speaker !== lastSpeaker
    // Skip the break on the very first row — there's no prior line to break
    // away from, so it would just render as a stray leading blank line. This
    // matters most for providers with no speaker labels (google_live_translate,
    // chatgpt): their first token always carries a known `language` (the
    // fixed target language), so without the `idx > 0` guard this pane would
    // start one line lower than a sibling pane whose first token has no
    // language yet (e.g. an auto-detect source pane).
    const isNewLanguage = idx > 0 && token.language != null && token.language !== lastLanguage
    const isEndToken = token.text === '<end>'
    const isLanguageChangeBreak = token.text === LANGUAGE_CHANGE_BREAK_TOKEN_TEXT
    const { draftTokenClass, finalTokenClass } = stylesForToken(token)
    const isRealSegment =
      Boolean(showTimestamp) && token.is_final && !isEndToken && !isLanguageChangeBreak
    // A finalized segment shows its own marker either because the speaker
    // just changed (always) or because 30s have passed since whichever
    // marker last showed, mid-turn — the still-streaming draft token is never
    // a candidate since it isn't `is_final` yet.
    const showsPeriodicMarker =
      isRealSegment &&
      !isNewSpeaker &&
      typeof token.start_ms === 'number' &&
      (lastMarkerMs === undefined || token.start_ms - lastMarkerMs >= PERIODIC_MARKER_MS)
    // Never break before the first row: there is no previous line to leave, so it would render
    // as a stray blank line at the top of the pane.
    const startsNewLine = idx > 0 && (previousEndedSentence || showsPeriodicMarker)

    if (isLanguageChangeBreak) {
      // Force the *next* row to open a new line regardless of speaker/language/sentence-end —
      // this synthetic token renders nothing itself (see `TokenRow`).
      previousEndedSentence = true
    } else if (!isEndToken) {
      lastSpeaker = token.speaker
      lastLanguage = token.language
      previousEndedSentence = endsSentence(token.text)
    }

    if (isRealSegment && (isNewSpeaker || showsPeriodicMarker) && typeof token.start_ms === 'number') {
      lastMarkerMs = token.start_ms
    }

    return (
      <TokenRow
        key={isEndToken ? `end-token-${idx}` : `${keyPrefix}-${idx}`}
        token={token}
        isNewSpeaker={isNewSpeaker}
        isNewLanguage={isNewLanguage}
        startsNewLine={startsNewLine}
        finalTokenClass={finalTokenClass}
        draftTokenClass={draftTokenClass}
        showTimestamp={showTimestamp}
        showsPeriodicTimestamp={showsPeriodicMarker}
        highlightOnArrival={highlightOnArrival}
        highlightFromSentenceIndex={
          newestUtterance && idx >= newestUtterance.startIndex && idx <= newestUtterance.endIndex
            ? idx === newestUtterance.startIndex
              ? newestUtterance.sentenceIndex
              : 0
            : undefined
        }
      />
    )
  })
}
